import {
    Component,
    Prop,
    h,
    Host,
    Event,
    EventEmitter,
    Watch,
    ComponentInterface,
    Element,
    State,
    Listen,
} from "@stencil/core";

const EASING = (pos) => -(Math.pow(pos - 1, 4) - 1);
const MAX_ACCELERATION = 10;
const SENSITIVITY = 0.8;

export interface IScrollPickerItem {
    value: any;
    text: string | number;
}

/**
 * This scroll picker is inspired by this code pen: https://codepen.io/gnauhca/pen/JrdpZZ
 */
@Component({
    tag: "uic-scroll-picker",
    styleUrl: "scroll-picker.scss",
})
export class ScrollPicker implements ComponentInterface {
    @State() tick = {};

    @Element() private element: HTMLUicScrollPickerElement;

    /**
     * List of items to select from.
     */
    @Prop() items: IScrollPickerItem[] = [];

    /**
     * The value of the selected item
     */
    @Prop({ mutable: true }) value: any;

    /**
     * The unit for the items
     */
    @Prop() unit?: string = "";

    /**
     * Displayed number of items above and below the selected value
     */
    @Prop() additionalItemCount?: number = 3;

    /**
     * Boolean to disable the scroll-picker
     */
    @Prop() disabled?: boolean = false;

    /**
     * Event is fired when the value was changed
     */
    @Event({ eventName: "uicChange" }) change: EventEmitter<any>;

    @Watch("value")
    onValueChanged() {
        this.select(this.value, true);
    }

    @Watch("items")
    onItemsChanged() {
        if (!this.isMoving) {
            this.selectByScroll(this.currentScrollItemIndex);
        }
    }

    private selectedItem: IScrollPickerItem;
    private currentScrollItemIndex: number;

    private acceleration = SENSITIVITY * 10;
    private animationFrameReference: number = 0;
    private isMoving: boolean = false;

    // Use to capture touchmove/mousemove events after touchstart/mousedown
    private isScrolling: boolean = false;

    private itemHeight: number = 35;
    private oldItemHeight: number = 35;

    private touchData: {
        startY: number;
        yArr: any[];
        touchScroll: number;
    };

    constructor() {}

    componentDidLoad() {
        this.selectedItem = this.items[0];
        this.currentScrollItemIndex = 0;

        this.touchData = {
            startY: 0,
            yArr: [],
            touchScroll: 0,
        };

        this.tick = {}; // Force a rerender

        if (this.items?.length > 0) {
            this.value = this.value !== null ? this.value : this.items[0].value;
            setTimeout(() => {
                this.select(this.value);
            });
        }

        this.oldItemHeight = this.itemHeight;
        this.updateItemHeight();
    }

    disconnectedCallback() {
        this.stop();
    }

    @Listen("wheel", { passive: false })
    wheel(e: WheelEvent) {
        e.preventDefault();
        if (e.deltaY > 0) {
            this.moveToElement(1);
        } else {
            this.moveToElement(-1);
        }
    }

    @Listen("keydown", { passive: false })
    keydown(e: KeyboardEvent) {
        if (e.key === "ArrowUp") {
            e.preventDefault();
            this.moveToElement(-1);
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            this.moveToElement(1);
        }
    }

    @Listen("touchstart", { passive: false })
    _touchstart(e: TouchEvent) {
        e.preventDefault();
        this.touchstart(e);
    }

    @Listen("mousedown", { passive: true })
    touchstart(e: MouseEvent | TouchEvent) {
        this.isScrolling = true;
        let eventY =
            (e as MouseEvent).clientY || (e as TouchEvent).touches[0].clientY;
        this.touchData.startY = eventY;
        this.touchData.yArr = [[eventY, new Date().getTime()]];
        this.touchData.touchScroll = this.currentScrollItemIndex;
        this.stop();
    }

    @Listen("touchmove", { passive: false })
    _touchmove(e: TouchEvent) {
        e.preventDefault();
        this.touchmove(e);
    }

    @Listen("mousemove", { passive: true })
    touchmove(e: MouseEvent | TouchEvent) {
        if (this.isScrolling) {
            let eventY =
                (e as MouseEvent).clientY ||
                (e as TouchEvent).touches[0].clientY;
            this.touchData.yArr.push([eventY, new Date().getTime()]);
            if (this.touchData.yArr.length > 5) {
                this.touchData.yArr.unshift();
            }

            let scrollAdd = (this.touchData.startY - eventY) / this.itemHeight;
            let moveToScrollItemIndex = this.currentScrollItemIndex + scrollAdd;

            if (moveToScrollItemIndex < 0) {
                moveToScrollItemIndex *= 0.3;
            } else if (moveToScrollItemIndex > this.items.length) {
                moveToScrollItemIndex =
                    this.items.length +
                    (moveToScrollItemIndex - this.items.length) * 0.3;
            }

            this.touchData.touchScroll = this.moveTo(moveToScrollItemIndex);
        }
    }

    @Listen("touchend", { passive: false })
    _touchend(e: TouchEvent) {
        e.preventDefault();
        this.touchend();
    }

    @Listen("mouseup", { passive: true })
    @Listen("mouseleave", { passive: true })
    touchend() {
        this.isScrolling = false;

        if (this.touchData.yArr.length <= 0) {
            return;
        }

        let velocity;
        if (this.touchData.yArr.length === 1) {
            velocity = 0;
        } else {
            let startTime = this.touchData.yArr[
                this.touchData.yArr.length - 2
            ][1];
            let endTime = this.touchData.yArr[
                this.touchData.yArr.length - 1
            ][1];
            let startY = this.touchData.yArr[this.touchData.yArr.length - 2][0];
            let endY = this.touchData.yArr[this.touchData.yArr.length - 1][0];

            // Calculate the velocity
            velocity =
                (((startY - endY) / this.itemHeight) * 1000) /
                (endTime - startTime);
            let sign = velocity > 0 ? 1 : -1;

            velocity = Math.abs(velocity) > 30 ? 30 * sign : velocity;
        }
        this.currentScrollItemIndex = this.touchData.touchScroll;
        this.touchData.yArr = [];
        this.animateMoveByInitialVelocity(velocity);
    }

    @Listen("resize", { target: "window" })
    updateItemHeight() {
        if (window.innerWidth >= 1920) {
            this.itemHeight = 43;
        } else if (window.innerWidth < 1920 && window.innerWidth >= 1025) {
            this.itemHeight = 35;
        } else {
            this.itemHeight = 25;
        }

        if (this.oldItemHeight !== this.itemHeight) {
            this.tick = {};
            this.select(this.value);
            this.oldItemHeight = this.itemHeight;
        }
    }

    private moveToElement(direction: number) {
        const nextIndex =
            this.items.findIndex((x) => x.value === this.value) + direction;
        if (nextIndex >= 0 && nextIndex < this.items.length)
            this.select(this.items[nextIndex].value);
    }

    private async animateMoveByInitialVelocity(initV: number) {
        let initScrollIndex: number, finalScrollIndex: number;
        let totalScrollLen: number, a: number, t: number;

        if (
            this.currentScrollItemIndex < 0 ||
            this.currentScrollItemIndex > this.items.length - 1
        ) {
            a = MAX_ACCELERATION;
            initScrollIndex = this.currentScrollItemIndex;
            finalScrollIndex =
                this.currentScrollItemIndex < 0 ? 0 : this.items.length - 1;
            totalScrollLen = initScrollIndex - finalScrollIndex;

            t = Math.sqrt(Math.abs(totalScrollLen / a));
            initV = a * t;
            initV = this.currentScrollItemIndex > 0 ? -initV : initV;
            await this.animateToScroll(initScrollIndex, finalScrollIndex, t);
        } else {
            initScrollIndex = this.currentScrollItemIndex;
            a = initV > 0 ? -this.acceleration : this.acceleration; // Decelerate or accelerate
            t = Math.abs(initV / a);
            totalScrollLen = initV * t + (a * t * t) / 2;
            finalScrollIndex = Math.round(
                this.currentScrollItemIndex + totalScrollLen
            ); // Round to ensure accuracy and finally scroll to an integer position
            finalScrollIndex =
                finalScrollIndex < 0
                    ? 0
                    : finalScrollIndex > this.items.length - 1
                    ? this.items.length - 1
                    : finalScrollIndex;

            totalScrollLen = finalScrollIndex - initScrollIndex;
            t = Math.sqrt(Math.abs(totalScrollLen / a));
            await this.animateToScroll(
                this.currentScrollItemIndex,
                finalScrollIndex,
                t
            );
        }

        this.selectByScroll(this.currentScrollItemIndex);
    }

    private animateToScroll(
        initScrollItem: number,
        finalScrollItem: number,
        t: number
    ) {
        if (initScrollItem === finalScrollItem || t === 0) {
            this.moveTo(initScrollItem);
            return;
        }

        let start = new Date().getTime() / 1000;
        let pass = 0;
        let totalScrollLen = finalScrollItem - initScrollItem;

        return new Promise<void>((resolve) => {
            this.isMoving = true;
            let tick = () => {
                pass = new Date().getTime() / 1000 - start;

                if (pass < t) {
                    this.currentScrollItemIndex = this.moveTo(
                        initScrollItem + EASING(pass / t) * totalScrollLen
                    );
                    this.animationFrameReference = requestAnimationFrame(tick);
                } else {
                    resolve();
                    this.stop();
                    this.currentScrollItemIndex = this.moveTo(
                        initScrollItem + totalScrollLen
                    );
                }
            };
            tick();
        });
    }

    private stop() {
        this.isMoving = false;
        cancelAnimationFrame(this.animationFrameReference);
    }

    private selectByScroll(_scroll, noEmit = false) {
        let normalizedScroll = _scroll | 0;

        while (normalizedScroll < 0) {
            normalizedScroll += this.items.length;
        }
        normalizedScroll = normalizedScroll % this.items.length;

        if (normalizedScroll > this.items.length - 1) {
            normalizedScroll = this.items.length - 1;
        }

        this.currentScrollItemIndex = this.moveTo(normalizedScroll);
        this.selectedItem = this.items[this.currentScrollItemIndex];
        this.value = this.selectedItem.value;
        if (!noEmit) {
            this.change.emit(this.value);
        }
    }

    private moveTo(scrollPosition: number): number {
        const selectOptions = this.element.querySelector(
            ".select-options"
        ) as HTMLElement;
        if (selectOptions !== null) {
            selectOptions.style.transform = `translate(0, ${
                -scrollPosition * this.itemHeight +
                this.additionalItemCount * this.itemHeight -
                4
            }px)`;
        }

        const highlightList = this.element.querySelector(
            ".highlight-list"
        ) as HTMLElement;
        if (highlightList !== null) {
            highlightList.style.transform = `translate(0, ${
                -scrollPosition * this.itemHeight
            }px)`;
        }

        [...this.element.querySelectorAll(".select-option")]
            .map((x) => x as HTMLElement)
            .forEach((itemElem) => {
                if (
                    Math.abs(
                        parseInt(itemElem.getAttribute("data-index")) -
                            scrollPosition
                    ) >
                    this.additionalItemCount + 2
                ) {
                    itemElem.style.visibility = "hidden";
                } else {
                    itemElem.style.visibility = "visible";
                }
            });

        return scrollPosition;
    }

    private select(value: any, noEmit = false) {
        const itemIndex = this.items.findIndex((x) => x.value === value);
        if (itemIndex >= 0) {
            window.cancelAnimationFrame(this.animationFrameReference);
            this.selectByScroll(itemIndex, noEmit);
        } else {
            throw new Error(
                `Can not select value: ${value}, ${value} match nothing in any value in provided items`
            );
        }
    }

    render() {
        return (
            <Host
                class={
                    this.disabled !== false
                        ? "uic-scroll-picker disabled"
                        : "uic-scroll-picker"
                }
            >
                <div
                    class="select-wrap"
                    tabIndex={this.disabled !== false ? -1 : 1}
                    style={{
                        minHeight: `${
                            this.itemHeight * (this.additionalItemCount * 2 + 1)
                        }px`,
                    }}
                >
                    <ul class="select-options">
                        {this.items.map((item, i) => (
                            <li
                                class={
                                    this.unit !== ""
                                        ? "select-option select-option__with-unit"
                                        : "select-option"
                                }
                                data-index={i}
                                style={{
                                    top: `${this.itemHeight * -0.5}px`,
                                    height: `${this.itemHeight}px`,
                                    lineHeight: `${this.itemHeight}px`,
                                }}
                            >
                                {item.text}
                                {this.unit !== "" ? (
                                    <span class="unit-placeholder">
                                        {this.unit}
                                    </span>
                                ) : (
                                    ""
                                )}
                            </li>
                        ))}
                    </ul>
                    <div
                        class="highlight"
                        style={{
                            height: `${this.itemHeight + 5}px`,
                            lineHeight: `${this.itemHeight + 5}px`,
                        }}
                    >
                        <div class="highlight-background">
                            {this.unit !== "" ? (
                                <span class="highlight-unit">{this.unit}</span>
                            ) : (
                                ""
                            )}
                            <ul class="highlight-list">
                                {this.items.map((item) => (
                                    <li
                                        class={
                                            this.unit !== ""
                                                ? "highlight-item highlight-item__with-unit"
                                                : "highlight-item"
                                        }
                                        style={{
                                            height: `${this.itemHeight}px`,
                                        }}
                                    >
                                        {item.text}
                                        {this.unit !== "" ? (
                                            <span class="unit-placeholder">
                                                {this.unit}
                                            </span>
                                        ) : (
                                            ""
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
